// Timeline — the overall time-progress indicator on the cooking screen.
//
// Two shapes share one component:
//
// 1. Progress indicator (the cooking screen's headline use, README §6.7.3
//    item 2 reinterpreted as a progress bar). When `startLabel`,
//    `finishLabel` and `progress` are supplied it renders:
//
//       <start>  ──────●(now chip)────────  <finish>
//
//    The start time anchors the left end, the expected finish anchors the
//    right end, and a present-time chip rides the line at `progress` (a
//    0..1 fraction of elapsed wall-clock between the two). On overrun the
//    fraction is clamped to 1 so the chip parks at the finish rather than
//    sailing off the end — the plan does not move (README §1, §6.7.3).
//
// 2. Bare band (the original primitive). With no start/finish/progress it
//    falls back to the quiet "now" band: a dot, a line and an optional
//    `nowLabel`. Retained so the component stays a usable primitive and
//    the Phase-4 behaviour is preserved.
//
// `mode`:
// - `prep`     — "Prep" label.
// - `liveCook` — "LiveCook" label.
//
// Per README §7.2 the only motion permitted is a quiet pulse on the
// present-time marker; the reduced-motion variant strips it. The
// accompanying CSS rule lives in styles.css (`cc-timeline-dot`).

import { theme } from '../theme';
import { Chip } from './Chip';

export type TimelineMode = 'prep' | 'liveCook';

export interface TimelineProps {
  mode: TimelineMode;
  /** Present-time label, shown in the chip on the line (or at the right
   *  edge of the bare band). */
  nowLabel?: string;
  /** Start-of-cook label, anchored at the left end. Enables the progress
   *  shape when supplied together with `finishLabel`. */
  startLabel?: string;
  /** Expected-finish label, anchored at the right end. */
  finishLabel?: string;
  /** 0..1 position of the present-time chip between start and finish.
   *  Values outside the range are clamped. Defaults to 0. */
  progress?: number;
  /** When set, renders the per-phase ROW variant: a phase chip at the left
   *  (no static start-time label), the present-time chip riding the line and
   *  edge-clamped so it tucks beside the phase chip at the start and beside
   *  the finish label at the end, and `finishLabel` anchored right. */
  phaseChip?: string;
}

function clamp01(n: number): number {
  if (!Number.isFinite(n)) return 0;
  return Math.max(0, Math.min(1, n));
}

export function Timeline({
  mode,
  nowLabel,
  startLabel,
  finishLabel,
  progress,
  phaseChip,
}: TimelineProps) {
  const labelText = mode === 'prep' ? 'Prep' : 'LiveCook';
  // Per-phase row variant takes precedence: phase chip + finish, no start.
  const isPhaseRow = phaseChip !== undefined && finishLabel !== undefined;
  const isProgress = !isPhaseRow && startLabel !== undefined && finishLabel !== undefined;

  const ariaLabel = isPhaseRow
    ? `Cooking progress, ${phaseChip}${nowLabel ? `, now ${nowLabel}` : ''}${
        finishLabel ? `, finishes ${finishLabel}` : ''
      }`
    : isProgress
      ? `Cooking progress, ${labelText} mode, ${startLabel} to ${finishLabel}${
          nowLabel ? `, now ${nowLabel}` : ''
        }`
      : `Present time, ${labelText} mode${nowLabel ? `, ${nowLabel}` : ''}`;

  const endLabelStyle = {
    fontSize: '0.85rem',
    color: theme.text.muted,
    fontVariantNumeric: 'tabular-nums' as const,
    flex: '0 0 auto',
  };

  if (isPhaseRow) {
    const pct = clamp01(progress ?? 0) * 100;
    return (
      <div
        role="separator"
        aria-label={ariaLabel}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          width: '100%',
          minHeight: '28px',
        }}
      >
        <Chip text={phaseChip} variant="lane" ariaLabel={phaseChip} />
        <div
          aria-hidden="true"
          style={{
            position: 'relative',
            flex: 1,
            height: '3px',
            background: theme.accent.action,
            borderRadius: '2px',
          }}
        >
          {/* Present-time chip — rides the line at `progress`, edge-clamped
              via `translateX(-pct%)` so it stays within the track: left edge
              at the start, right edge at the finish. */}
          <span
            className="cc-timeline-dot"
            style={{
              position: 'absolute',
              top: '50%',
              left: `${pct}%`,
              transform: `translate(-${pct}%, -50%)`,
              background: theme.accent.action,
              color: theme.surface.onAccent,
              fontSize: '0.7rem',
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1,
              padding: '0.2rem 0.4rem',
              borderRadius: '999px',
              whiteSpace: 'nowrap',
            }}
          >
            {nowLabel ?? ''}
          </span>
        </div>
        <span aria-hidden="true" style={endLabelStyle}>
          {finishLabel}
        </span>
      </div>
    );
  }

  if (isProgress) {
    const pct = clamp01(progress ?? 0) * 100;
    return (
      <div
        role="separator"
        aria-label={ariaLabel}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          width: '100%',
          minHeight: '28px',
        }}
      >
        <span aria-hidden="true" style={endLabelStyle}>
          {startLabel}
        </span>
        <div
          aria-hidden="true"
          style={{
            position: 'relative',
            flex: 1,
            height: '3px',
            background: theme.accent.action,
            borderRadius: '2px',
          }}
        >
          {/* Present-time chip — rides the line at `progress`. */}
          <span
            className="cc-timeline-dot"
            style={{
              position: 'absolute',
              top: '50%',
              left: `${pct}%`,
              transform: 'translate(-50%, -50%)',
              background: theme.accent.action,
              color: theme.surface.onAccent,
              fontSize: '0.7rem',
              fontWeight: 600,
              fontVariantNumeric: 'tabular-nums',
              lineHeight: 1,
              padding: '0.2rem 0.4rem',
              borderRadius: '999px',
              whiteSpace: 'nowrap',
            }}
          >
            {nowLabel ?? ''}
          </span>
        </div>
        <span aria-hidden="true" style={endLabelStyle}>
          {finishLabel}
        </span>
      </div>
    );
  }

  // Bare band (legacy primitive).
  return (
    <div
      role="separator"
      aria-label={ariaLabel}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        width: '100%',
        height: '20px',
        position: 'relative',
      }}
    >
      <span
        className="cc-timeline-dot"
        aria-hidden="true"
        style={{
          width: '10px',
          height: '10px',
          borderRadius: '50%',
          background: theme.accent.action,
          flex: '0 0 auto',
        }}
      />
      <div
        aria-hidden="true"
        style={{
          flex: 1,
          height: '3px',
          background: theme.accent.action,
          borderRadius: '2px',
        }}
      />
      {nowLabel && (
        <span
          style={{
            fontSize: '0.85rem',
            color: theme.text.muted,
            fontVariantNumeric: 'tabular-nums',
            flex: '0 0 auto',
          }}
        >
          {nowLabel}
        </span>
      )}
    </div>
  );
}

export default Timeline;
